// src/pages/surveys/SurveyList.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection, query, where, onSnapshot, doc, updateDoc, deleteDoc, getDocs, addDoc, serverTimestamp
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../../contexts/AuthContext';

// Role display labels
const ROLE_LABELS = {
  admin: 'Admin',
  dean: 'Dean',
  faculty: 'Faculty',
  student: 'Student',
  superadmin: 'Super Admin',
};

const s = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 },
  title: { fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 700, color: 'var(--ka-navy)', marginBottom: 4 },
  sub: { fontSize: 14, color: 'var(--ka-gray-600)' },
  newBtn: {
    padding: '10px 22px', background: 'var(--ka-navy)', color: 'white',
    border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700,
    cursor: 'pointer', fontFamily: 'var(--font-body)', transition: 'all 0.15s',
    flexShrink: 0,
  },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 },
  card: {
    background: 'var(--ka-white)', border: '1px solid var(--ka-gray-200)',
    borderRadius: 12, overflow: 'hidden', boxShadow: 'var(--shadow-sm)',
    transition: 'all 0.15s', cursor: 'pointer',
  },
  cardAccent: { height: 6 },
  cardBody: { padding: '18px 20px' },
  cardTitle: { fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 700, color: 'var(--ka-navy)', marginBottom: 4, lineHeight: 1.3 },
  cardDesc: { fontSize: 13, color: 'var(--ka-gray-600)', marginBottom: 12, lineHeight: 1.5 },
  cardMeta: { display: 'flex', gap: 16, fontSize: 12, color: 'var(--ka-gray-400)', marginBottom: 12 },
  cardFooter: {
    display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
    padding: '10px 16px', borderTop: '1px solid var(--ka-gray-100)',
    background: 'var(--ka-gray-50)',
  },
  statusPill: {
    padding: '3px 10px', borderRadius: 20, fontSize: 10,
    fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
  },
  sharedPill: {
    padding: '3px 10px', borderRadius: 20, fontSize: 10,
    fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
    background: '#ede9fe', color: '#6d28d9', marginLeft: 6, flexShrink: 0,
  },
  actionBtn: {
    padding: '5px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
    cursor: 'pointer', border: '1px solid var(--ka-gray-200)',
    background: 'var(--ka-white)', color: 'var(--ka-gray-600)',
    fontFamily: 'var(--font-body)', transition: 'all 0.1s', whiteSpace: 'nowrap',
  },
  empty: {
    gridColumn: '1/-1', textAlign: 'center', padding: '60px 20px',
    background: 'var(--ka-white)', borderRadius: 12, border: '1px dashed var(--ka-gray-200)',
  },
  filterRow: { display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' },
  filterBtn: {
    padding: '7px 16px', borderRadius: 20, fontSize: 13, fontWeight: 600,
    cursor: 'pointer', border: '1.5px solid var(--ka-gray-200)',
    background: 'var(--ka-white)', color: 'var(--ka-gray-400)',
    fontFamily: 'var(--font-body)', transition: 'all 0.1s',
  },
  filterBtnActive: {
    background: 'var(--ka-navy)', color: 'white', borderColor: 'var(--ka-navy)',
  },
  // Share modal
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
    zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 16,
  },
  modal: {
    background: 'white', borderRadius: 14, padding: 28,
    width: '100%', maxWidth: 460, boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
  },
  modalTitle: {
    fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700,
    color: 'var(--ka-navy)', marginBottom: 6,
  },
  modalSub: { fontSize: 13, color: 'var(--ka-gray-400)', marginBottom: 20 },
  inputRow: { display: 'flex', gap: 8, marginBottom: 16 },
  input: {
    flex: 1, padding: '9px 12px', border: '1px solid var(--ka-gray-200)',
    borderRadius: 7, fontSize: 13, fontFamily: 'var(--font-body)',
    color: 'var(--ka-navy)', outline: 'none',
  },
  addBtn: {
    padding: '9px 16px', background: 'var(--ka-navy)', color: 'white',
    border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 700,
    cursor: 'pointer', fontFamily: 'var(--font-body)', flexShrink: 0,
  },
  shareRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '8px 0', borderBottom: '1px solid var(--ka-gray-100)',
    fontSize: 13, color: 'var(--ka-navy)',
  },
  removeBtn: {
    background: 'none', border: 'none', color: 'var(--ka-gray-400)',
    cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: '2px 6px',
    borderRadius: 4, fontFamily: 'var(--font-body)',
  },
  modalFooter: { display: 'flex', justifyContent: 'flex-end', marginTop: 20 },
  closeBtn: {
    padding: '8px 20px', background: 'var(--ka-gray-100)', color: 'var(--ka-navy)',
    border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 600,
    cursor: 'pointer', fontFamily: 'var(--font-body)',
  },
  errorMsg: { fontSize: 12, color: '#ef4444', marginTop: -10, marginBottom: 10 },
  sectionHeader: {
    display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
    padding: '10px 0', marginBottom: 4, userSelect: 'none',
  },
  sectionTitle: {
    fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 700, color: 'var(--ka-navy)',
  },
  sectionCount: {
    fontSize: 12, fontWeight: 600, color: 'var(--ka-gray-400)',
    background: 'var(--ka-gray-100)', padding: '2px 8px', borderRadius: 10,
  },
  sectionChevron: {
    marginLeft: 'auto', fontSize: 13, color: 'var(--ka-gray-400)', transition: 'transform 0.2s',
  },
  sectionBlock: {
    borderTop: '2px solid var(--ka-gray-100)', marginBottom: 28, paddingTop: 4,
  },
};

const STATUS_COLORS = {
  active: { bg: '#d1fae5', color: '#065f46' },
  draft: { bg: '#f3f4f6', color: '#6b7280' },
  closed: { bg: '#fee2e2', color: '#991b1b' },
};

// ─── Collapsible section wrapper ───────────────────────────────────────────────
function SurveySection({ title, count, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={s.sectionBlock}>
      <div style={s.sectionHeader} onClick={() => setOpen(o => !o)}>
        <div style={s.sectionTitle}>{title}</div>
        <div style={s.sectionCount}>{count}</div>
        <div style={{ ...s.sectionChevron, transform: open ? 'rotate(0deg)' : 'rotate(-90deg)' }}>▼</div>
      </div>
      {open && children}
    </div>
  );
}

export default function SurveyList() {
  const navigate = useNavigate();
  const { user, profile } = useAuth();

  // Owned surveys
  const [ownedSurveys, setOwnedSurveys] = useState([]);
  // Surveys shared with me by UID
  const [sharedWithMeSurveys, setSharedWithMeSurveys] = useState([]);
  // Surveys accessible by my role (accessRoles contains my role)
  const [roleSurveys, setRoleSurveys] = useState([]);
  // All surveys (superadmin only)
  const [allSurveys, setAllSurveys] = useState([]);

  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [hoveredCard, setHoveredCard] = useState(null);

  const isSuperAdmin = profile?.role === 'superadmin' || profile?.superadmin === true;
  const userRole = profile?.role;

  // Share modal state
  const [shareModal, setShareModal] = useState(null);
  const [shareEmail, setShareEmail] = useState('');
  const [shareError, setShareError] = useState('');
  const [shareLoading, setShareLoading] = useState(false);
  const [staffSuggestions, setStaffSuggestions] = useState([]);
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState(true); // "notify by email" checkbox
  const [notifyStatus, setNotifyStatus] = useState(null); // null | 'sending' | 'sent' | 'error'

  useEffect(() => {
    if (!user?.uid) return;

    const toSurvey = (d, flags = {}) => ({ id: d.id, ...d.data(), ...flags });
    const sortDesc = arr => [...arr].sort((a, b) =>
      (b.updatedAt?.toMillis?.() || 0) - (a.updatedAt?.toMillis?.() || 0)
    );

    const unsubs = [];

    // 1. My surveys
    unsubs.push(onSnapshot(
      query(collection(db, 'surveys'), where('createdBy', '==', user.uid)),
      snap => { setOwnedSurveys(sortDesc(snap.docs.map(d => toSurvey(d, { _owned: true })))); setLoading(false); }
    ));

    // 2. Shared with me by UID
    unsubs.push(onSnapshot(
      query(collection(db, 'surveys'), where('sharedWith', 'array-contains', user.uid)),
      snap => { setSharedWithMeSurveys(sortDesc(snap.docs.map(d => toSurvey(d)))); }
    ));

    // 3. Surveys shared with my role (accessRoles array-contains my role)
    //    Skip if no role or superadmin (they get "All Surveys" instead)
    if (userRole && !isSuperAdmin) {
      unsubs.push(onSnapshot(
        query(collection(db, 'surveys'), where('settings.accessRoles', 'array-contains', userRole)),
        snap => { setRoleSurveys(sortDesc(snap.docs.map(d => toSurvey(d)))); }
      ));
    }

    // 4. All surveys (superadmin only)
    if (isSuperAdmin) {
      unsubs.push(onSnapshot(
        collection(db, 'surveys'),
        snap => { setAllSurveys(sortDesc(snap.docs.map(d => toSurvey(d)))); }
      ));
    }

    return () => unsubs.forEach(u => u());
  }, [user?.uid, userRole, isSuperAdmin]);

  // Filter helper
  const applyFilter = surveys => filter === 'all' ? surveys : surveys.filter(sv => sv.status === filter);

  // For superadmin "All Surveys": group by creator, excluding their own (shown in My Surveys)
  const othersSurveys = isSuperAdmin
    ? allSurveys.filter(sv => sv.createdBy !== user.uid)
    : [];

  // Group others by creator name for superadmin view
  const byCreator = othersSurveys.reduce((acc, sv) => {
    const name = sv.createdByName || sv.createdBy || 'Unknown';
    if (!acc[name]) acc[name] = [];
    acc[name].push(sv);
    return acc;
  }, {});

  // Surveys shared with me by UID, excluding ones I own
  const sharedFiltered = sharedWithMeSurveys.filter(sv => sv.createdBy !== user.uid);

  // Role-access surveys: exclude ones I created or that are already in sharedWithMe
  const sharedWithMeIds = new Set(sharedWithMeSurveys.map(sv => sv.id));
  const roleSurveysFiltered = roleSurveys.filter(
    sv => sv.createdBy !== user.uid && !sharedWithMeIds.has(sv.id)
  );

  const roleLabel = ROLE_LABELS[userRole] || (userRole ? userRole.charAt(0).toUpperCase() + userRole.slice(1) : '');

  const handleDuplicate = async (e, survey) => {
    e.stopPropagation();
    try {
      const newDoc = await addDoc(collection(db, 'surveys'), {
        title: `Copy of ${survey.title || 'Untitled Survey'}`,
        description: survey.description || '',
        questions: survey.questions || [],
        settings: { ...(survey.settings || {}), },
        status: 'draft',
        createdBy: user.uid,
        createdByName: profile?.displayName || user.displayName || user.email || '',
        sharedWith: [],
        sharedWithNames: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      navigate(`/tools/surveys/edit/${newDoc.id}`);
    } catch (err) {
      console.error('Duplicate failed:', err);
      alert('Could not duplicate the survey. Please try again.');
    }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Delete this survey? This cannot be undone.')) return;
    await deleteDoc(doc(db, 'surveys', id));
  };

  const handleToggleStatus = async (e, survey) => {
    e.stopPropagation();
    const newStatus = survey.status === 'active' ? 'draft' : 'active';
    await updateDoc(doc(db, 'surveys', survey.id), { status: newStatus });
  };

  // Share modal handlers
  const openShare = (e, survey) => {
    e.stopPropagation();
    setShareModal(survey);
    setShareEmail('');
    setShareError('');
    setNotifyStatus(null);
  };
  const closeShare = () => {
    setShareModal(null); setShareEmail(''); setShareError('');
    setStaffSuggestions([]); setNotifyStatus(null);
  };

  // Search staff as user types (min 2 chars)
  useEffect(() => {
    if (!shareModal) return;
    const term = shareEmail.trim().toLowerCase();
    if (term.length < 2) { setStaffSuggestions([]); return; }
    setSuggestionLoading(true);
    const timer = setTimeout(async () => {
      try {
        const snap = await getDocs(collection(db, 'staff'));
        const matches = snap.docs
          .map(d => ({ uid: d.id, ...d.data() }))
          .filter(p => {
            const name = (p.displayName || p.name || '').toLowerCase();
            const email = (p.email || '').toLowerCase();
            return name.includes(term) || email.includes(term);
          })
          .filter(p => !(shareModal.sharedWith || []).includes(p.uid))
          .slice(0, 6);
        setStaffSuggestions(matches);
      } catch (e) { setStaffSuggestions([]); }
      setSuggestionLoading(false);
    }, 250);
    return () => clearTimeout(timer);
  }, [shareEmail, shareModal]);

  const addSharePerson = async (targetUid, displayName, email) => {
    const current = shareModal.sharedWith || [];
    const currentNames = shareModal.sharedWithNames || [];
    if (current.includes(targetUid)) {
      setShareError('Already shared with this person.');
      return;
    }
    setShareLoading(true);
    try {
      const updated = [...current, targetUid];
      const updatedNames = [...currentNames, { uid: targetUid, email, name: displayName }];
      await updateDoc(doc(db, 'surveys', shareModal.id), { sharedWith: updated, sharedWithNames: updatedNames });
      setShareModal(prev => ({ ...prev, sharedWith: updated, sharedWithNames: updatedNames }));
      setShareEmail('');
      setStaffSuggestions([]);
      setShareError('');

      // Send email notification if checkbox is on
      if (notifyEmail && email) {
        setNotifyStatus('sending');
        try {
          const notifyFn = httpsCallable(getFunctions(), 'notifySurveyShare');
          await notifyFn({
            surveyId: shareModal.id,
            surveyTitle: shareModal.title || 'Untitled Survey',
            recipientEmail: email,
            recipientName: displayName || '',
            sharerName: profile?.displayName || user.displayName || user.email || 'A colleague',
            sharerEmail: profile?.email || user.email || '',
          });
          setNotifyStatus('sent');
        } catch (notifyErr) {
          console.error('Email notification failed:', notifyErr);
          setNotifyStatus('error');
        }
      }
    } catch (err) {
      console.error(err);
      setShareError('Something went wrong. Please try again.');
    }
    setShareLoading(false);
  };

  // Resolve a real Firebase Auth UID from email via /users collection
  const resolveAuthUid = async (email) => {
    const snap = await getDocs(query(collection(db, 'users'), where('email', '==', email)));
    if (!snap.empty) return { uid: snap.docs[0].id, name: snap.docs[0].data().displayName || null };
    return null;
  };

  const handleAddShare = async () => {
    const email = shareEmail.trim().toLowerCase();
    if (!email) return;
    setShareError('');
    setShareLoading(true);
    try {
      // Look up real Auth UID from /users by email
      const resolved = await resolveAuthUid(email);
      if (!resolved) { setShareError('No account found for that email address.'); setShareLoading(false); return; }
      // Get display name from /staff as fallback
      let name = resolved.name;
      if (!name) {
        const staffSnap = await getDocs(query(collection(db, 'staff'), where('email', '==', email)));
        if (!staffSnap.empty) {
          const d = staffSnap.docs[0].data();
          name = d.displayName || d.name || email;
        } else {
          name = email;
        }
      }
      await addSharePerson(resolved.uid, name, email);
    } catch (err) {
      console.error(err);
      setShareError('Something went wrong. Please try again.');
    }
    setShareLoading(false);
  };

  const handleRemoveShare = async (uid) => {
    const updated = (shareModal.sharedWith || []).filter(u => u !== uid);
    const updatedNames = (shareModal.sharedWithNames || []).filter(u => u.uid !== uid);
    await updateDoc(doc(db, 'surveys', shareModal.id), {
      sharedWith: updated,
      sharedWithNames: updatedNames,
    });
    setShareModal(prev => ({ ...prev, sharedWith: updated, sharedWithNames: updatedNames }));
  };

  const filterBtnStyle = (f) => ({ ...s.filterBtn, ...(filter === f ? s.filterBtnActive : {}) });

  const SurveyCard = ({ survey }) => {
    const isOwner = survey.createdBy === user.uid;
    const sc = STATUS_COLORS[survey.status] || STATUS_COLORS.draft;
    const accent = survey.settings?.accentColor || '#1a2744';
    const qCount = (survey.questions || []).filter(q => q.type !== 'section').length;
    const updatedAt = survey.updatedAt?.toDate?.()?.toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    }) || '—';
    return (
      <div
        style={{
          ...s.card,
          ...(hoveredCard === survey.id ? { boxShadow: 'var(--shadow-md)', transform: 'translateY(-2px)' } : {}),
        }}
        onClick={() => navigate(`/tools/surveys/results/${survey.id}`)}
        onMouseEnter={() => setHoveredCard(survey.id)}
        onMouseLeave={() => setHoveredCard(null)}
      >
        <div style={{ ...s.cardAccent, background: accent }} />
        <div style={s.cardBody}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
            <div style={s.cardTitle}>{survey.title || 'Untitled Survey'}</div>
            <span style={{ ...s.statusPill, background: sc.bg, color: sc.color, flexShrink: 0, marginLeft: 8 }}>
              {survey.status}
            </span>
          </div>
          {survey.description && (() => {
            const plain = survey.description.replace(/<[^>]+>/g, '');
            return <div style={s.cardDesc}>{plain.slice(0, 100)}{plain.length > 100 ? '…' : ''}</div>;
          })()}
          <div style={s.cardMeta}>
            <span>📝 {qCount} question{qCount !== 1 ? 's' : ''}</span>
            <span>📅 {updatedAt}</span>
            {!isOwner && <span>👤 {survey.createdByName || 'Unknown'}</span>}
          </div>
        </div>
        <div style={s.cardFooter} onClick={e => e.stopPropagation()}>
          {isOwner && (
            <button style={s.actionBtn} onClick={() => navigate(`/tools/surveys/edit/${survey.id}`)}>Edit</button>
          )}
          <button style={s.actionBtn} onClick={() => navigate(`/tools/surveys/results/${survey.id}`)}>Results</button>
          {isOwner && (
            <>
              <button style={s.actionBtn} onClick={(e) => handleToggleStatus(e, survey)}>
                {survey.status === 'active' ? 'Unpublish' : 'Publish'}
              </button>
              <button style={s.actionBtn} onClick={(e) => openShare(e, survey)}>Share</button>
              <button style={s.actionBtn} onClick={(e) => handleDuplicate(e, survey)}>Duplicate</button>
              <button
                style={{ ...s.actionBtn, color: '#ef4444', borderColor: '#fecaca', marginLeft: 'auto' }}
                onClick={(e) => handleDelete(e, survey.id)}
              >Delete</button>
            </>
          )}
        </div>
      </div>
    );
  };

  const SurveyGrid = ({ surveys, emptyMsg }) => {
    const list = applyFilter(surveys);
    if (list.length === 0) return (
      <div style={{ ...s.empty, marginBottom: 0 }}>
        <div style={{ fontSize: 13, color: 'var(--ka-gray-400)' }}>{emptyMsg || 'No surveys.'}</div>
      </div>
    );
    return (
      <div style={s.grid}>
        {list.map(sv => <SurveyCard key={sv.id} survey={sv} />)}
      </div>
    );
  };

  return (
    <div>
      <div style={s.header}>
        <div>
          <div style={s.title}>Surveys</div>
          <div style={s.sub}>Create and manage surveys, collect responses, and analyze results.</div>
        </div>
        <button style={s.newBtn} onClick={() => navigate('/tools/surveys/new')}>+ New Survey</button>
      </div>

      {/* Filter */}
      <div style={s.filterRow}>
        {['all', 'active', 'draft', 'closed'].map(f => (
          <button key={f} style={filterBtnStyle(f)} onClick={() => setFilter(f)}>
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ color: 'var(--ka-gray-400)', padding: 40, textAlign: 'center' }}>Loading surveys…</div>
      ) : (
        <>
          {/* My Surveys */}
          <SurveySection title="My Surveys" count={applyFilter(ownedSurveys).length} defaultOpen>
            <SurveyGrid
              surveys={ownedSurveys}
              emptyMsg={filter === 'all' ? 'No surveys yet. Create your first survey above.' : `No ${filter} surveys.`}
            />
          </SurveySection>

          {/* Shared with me by UID */}
          {sharedFiltered.length > 0 && (
            <SurveySection title="Shared with Me" count={applyFilter(sharedFiltered).length} defaultOpen>
              <SurveyGrid surveys={sharedFiltered} emptyMsg="No shared surveys match this filter." />
            </SurveySection>
          )}

          {/* Shared with my role */}
          {!isSuperAdmin && roleSurveysFiltered.length > 0 && (
            <SurveySection
              title={`Shared with ${roleLabel}`}
              count={applyFilter(roleSurveysFiltered).length}
              defaultOpen
            >
              <SurveyGrid surveys={roleSurveysFiltered} emptyMsg="No role-shared surveys match this filter." />
            </SurveySection>
          )}

          {/* All Surveys — superadmin, grouped by creator */}
          {isSuperAdmin && (
            <SurveySection title="All Surveys" count={applyFilter(othersSurveys).length} defaultOpen={false}>
              {Object.keys(byCreator).length === 0 ? (
                <div style={{ ...s.empty, marginBottom: 0 }}>
                  <div style={{ fontSize: 13, color: 'var(--ka-gray-400)' }}>No surveys from other users.</div>
                </div>
              ) : (
                Object.entries(byCreator).map(([creatorName, creatorSurveys]) => (
                  <div key={creatorName} style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ka-gray-400)', marginBottom: 10 }}>
                      {creatorName}
                    </div>
                    <SurveyGrid surveys={creatorSurveys} />
                  </div>
                ))
              )}
            </SurveySection>
          )}
        </>
      )}

      {/* Share Modal */}
      {shareModal && (
        <div style={s.overlay} onClick={closeShare}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalTitle}>Share "{shareModal.title || 'Untitled Survey'}"</div>
            <div style={s.modalSub}>
              Add a KA staff email to give them view + results access. They won't be able to edit or delete.
            </div>

            <div style={{ position: 'relative', marginBottom: 8 }}>
              <div style={s.inputRow}>
                <input
                  style={s.input}
                  type="text"
                  placeholder="Search by name or email…"
                  value={shareEmail}
                  onChange={e => { setShareEmail(e.target.value); setShareError(''); }}
                  onKeyDown={e => e.key === 'Enter' && handleAddShare()}
                  autoFocus
                  autoComplete="off"
                />
                <button style={{ ...s.addBtn, opacity: shareLoading ? 0.6 : 1 }} onClick={handleAddShare} disabled={shareLoading}>
                  {shareLoading ? '…' : 'Add'}
                </button>
              </div>
              {/* Autocomplete dropdown */}
              {(staffSuggestions.length > 0 || suggestionLoading) && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 60,
                  background: 'white', border: '1px solid var(--ka-gray-200)',
                  borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                  zIndex: 10, overflow: 'hidden',
                }}>
                  {suggestionLoading && (
                    <div style={{ padding: '10px 14px', fontSize: 13, color: 'var(--ka-gray-400)' }}>Searching…</div>
                  )}
                  {staffSuggestions.map(person => (
                    <div
                      key={person.uid}
                      style={{
                        padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid var(--ka-gray-100)',
                        display: 'flex', flexDirection: 'column', gap: 2,
                        transition: 'background 0.1s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--ka-gray-50)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'white'}
                      onMouseDown={async e => {
                        e.preventDefault(); // prevent blur before click
                        // Resolve real Auth UID from /users by email
                        const resolved = await resolveAuthUid(person.email);
                        const authUid = resolved?.uid || person.uid;
                        addSharePerson(authUid, person.displayName || person.name || person.email, person.email);
                      }}
                    >
                      <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--ka-navy)' }}>
                        {person.displayName || person.name || person.email}
                      </span>
                      <span style={{ fontSize: 12, color: 'var(--ka-gray-400)' }}>{person.email}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {shareError && <div style={s.errorMsg}>{shareError}</div>}

            {/* Current shares */}
            {(shareModal.sharedWithNames || []).length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ka-gray-400)', marginBottom: 8 }}>
                  Shared with
                </div>
                {(shareModal.sharedWithNames || []).map(entry => (
                  <div key={entry.uid} style={s.shareRow}>
                    <div>
                      {entry.name && <span style={{ fontWeight: 600, marginRight: 6 }}>{entry.name}</span>}
                      <span style={{ color: 'var(--ka-gray-400)', fontSize: 12 }}>{entry.email}</span>
                    </div>
                    <button style={s.removeBtn} onClick={() => handleRemoveShare(entry.uid)} title="Remove">×</button>
                  </div>
                ))}
              </div>
            )}
            {(shareModal.sharedWithNames || []).length === 0 && (
              <div style={{ fontSize: 13, color: 'var(--ka-gray-400)', fontStyle: 'italic', marginBottom: 8 }}>
                Not shared with anyone yet.
              </div>
            )}

            {/* Email notification toggle */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '12px 0', borderTop: '1px solid var(--ka-gray-100)', marginTop: 4,
            }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', flex: 1 }}>
                <input
                  type="checkbox"
                  checked={notifyEmail}
                  onChange={e => { setNotifyEmail(e.target.checked); setNotifyStatus(null); }}
                  style={{ width: 15, height: 15, accentColor: 'var(--ka-navy)', cursor: 'pointer' }}
                />
                <span style={{ fontSize: 13, color: 'var(--ka-navy)' }}>
                  Notify by email when sharing
                </span>
              </label>
              {notifyStatus === 'sending' && (
                <span style={{ fontSize: 12, color: 'var(--ka-gray-400)' }}>Sending…</span>
              )}
              {notifyStatus === 'sent' && (
                <span style={{ fontSize: 12, color: '#065f46', fontWeight: 600 }}>✓ Email sent</span>
              )}
              {notifyStatus === 'error' && (
                <span style={{ fontSize: 12, color: '#991b1b' }}>Email failed</span>
              )}
            </div>

            <div style={s.modalFooter}>
              <button style={s.closeBtn} onClick={closeShare}>Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}